import { extendZodWithOpenApi } from "zod-openapi";
extendZodWithOpenApi(z);

import { Zap } from "server/services/zapier/zaps/types/types";
import { z } from "zod";

export const formSubmissionZapCreateSchema = z
  .object({
    type: z.literal("form-submission"),
    form_id: z
      .string()
      .cuid()
      .describe("The ID (CUID) of the Form block that triggers the zap"),
  })
  .describe(
    "Input for setting up a zap that triggers when a form submission happens"
  );

export const newRecordZapCreateSchema = z
  .object({
    type: z.literal("new-record"),
    table_id: z
      .string()
      .describe("The ID (ULID) of the Zapier Table that triggers the zap"),
  })
  .describe(
    "Input for setting up a zap that triggers when a new record is created in a Zapier table"
  );

export const recordUpdatedCreateZapSchema = z
  .object({
    type: z.literal("record-updated"),
    table_id: z
      .string()
      .describe("The ID (ULID) of the Zapier Table that triggers the zap"),
  })
  .describe(
    "Input for setting up a zap that triggers when a record is updated in a Zapier table"
  );

export const recordFieldUpdatedCreateZapSchema = z
  .object({
    type: z.literal("record-field-updated"),
    table_id: z
      .string()
      .describe("The ID (ULID) of the Zapier Table that triggers the zap"),
    field_id: z
      .string()
      .describe(
        "The ID of the field in the Zapier Table. Has the format f<table-field-id>, e.g., f1, f2, etc."
      ),
    field_value: z.string().or(z.boolean()).optional(),
  })
  .describe(
    "Input for setting up a zap that triggers when a Zapier table record's field is updated to a specified value"
  );

export const buttonClickedZapSchema = z
  .object({
    type: z.literal("button-click"),
    block_id: z
      .string()
      .cuid()
      .describe("The ID (CUID) of the Button block that triggers the zap"),
  })
  .describe(
    "Input for setting up a zap that triggers when a button is clicked"
  );

export const paymentProcessedZapSchema = z
  .object({
    type: z.literal("stripe-payment-processed"),
    block_id: z
      .string()
      .cuid()
      .describe(
        "The ID (CUID) of the Stripe payment block that triggers the zap"
      ),
  })
  .describe(
    "Input for setting up a zap that triggers when a payment is processed through a Stripe payment block"
  );

export const CreateZapInputSchema = z
  .discriminatedUnion("type", [
    formSubmissionZapCreateSchema,
    newRecordZapCreateSchema,
    recordUpdatedCreateZapSchema,
    recordFieldUpdatedCreateZapSchema,
    buttonClickedZapSchema,
    paymentProcessedZapSchema,
  ])
  .openapi({
    ref: "CreateZapInput",
  });

export type CreateZapInput = z.infer<typeof CreateZapInputSchema>;

export const zapRelations = z.object({
  projects: z.string().cuid().array().optional(),
  pages: z.string().cuid().array().optional(),
  tables: z.string().array().optional(),
  forms: z.string().cuid().array().optional(),
  buttons: z.string().cuid().array().optional(),
  stripePayments: z.string().cuid().array().optional(),
  blockActions: z.string().cuid().array().optional(),
});
export type ZapRelations = z.infer<typeof zapRelations>;

export const ZapOutputSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    zdl: z.record(z.string(), z.any()).optional(),
    paused: z.boolean(),
    relations: zapRelations.optional(),
  })
  .openapi({ ref: "Zap" });

export type ZapOutput = z.infer<typeof ZapOutputSchema>;

export const zapToZapOutput = (
  zap: Zap,
  relations?: ZapRelations
): ZapOutput => ({
  id: zap.id,
  title: zap.title ?? "Untitled zap",
  paused: !zap.is_enabled,
  zdl: zap.current_version?.zdl ?? zap.draft?.zdl,
  relations,
});

export const zapRemovalOptions = ["disable-zap", "delete-zap"] as const;
export type ZapRemovalOption = (typeof zapRemovalOptions)[number];
